/**
 * Middleware para Chatbot Wizard v2
 * Maneja side effects: persistencia, timers, transiciones de estado
 */

import {
  CHATBOT_NAVIGATE,
  CHATBOT_SUBMIT_CONTACT,
  CHATBOT_SET_STEP,
  CHATBOT_TICK_TIMER,
  CHATBOT_CLEAR_HISTORY,
  CHATBOT_TOGGLE,
  chatbot,
} from './chatbot.actions';
import { saveChatHistory, clearChatHistory } from './utils/chatStorage';
import { services } from './chatbot.services';
import { getTopicRoute, getItemRoute } from './utils/contentRenderer';

const ADMIN_GROUP = 1;

let timerInterval = null;

/**
 * Decide si el usuario puede ser redirigido a una ruta.
 * Espeja el modelo de permisos de routes.js: el grupo admin abre todo, y una
 * ruta sin `requiresRole` es pública para cualquier usuario logueado.
 */
const canAccessRoute = (requiresRole, userGroups) => {
  if (requiresRole === undefined) return true;
  const groups = userGroups || [];
  return groups.includes(ADMIN_GROUP) || groups.includes(requiresRole);
};

/**
 * Resuelve a qué ruta de la app debe llevar una navegación del chat, si a alguna.
 * Solo redirigen los clics reales del usuario sobre una sección del menú o sobre
 * una pregunta. "Volver", "más preguntas" y los botones de navegación interna
 * llegan con el mismo `step` y no deben mover al usuario de donde está.
 */
const resolveRedirectTarget = (payload, currentSection) => {
  if (payload.fromMenu) return getTopicRoute(payload.section);
  if (payload.fromQuestion) return getItemRoute(currentSection, payload.question);
  return null;
};

/**
 * Navega a una sección de la app con carga completa de página.
 *
 * Es a propósito, no un descuido: el resto de la app navega igual. Los links
 * del sidebar son anchors (`SideBarScreen.js:284`) y el pase de personas a
 * group-trends hace `push` seguido de `window.location.reload()`
 * (`PersonsScreen.js:1071`). Varias pantallas comparten slices de Redux —
 * `Persons` y `GroupTrends` leen ambas `state.persons.results` — y cuentan con
 * el reset de REHYDRATE que solo ocurre al recargar (`persons.reducer.js:884`).
 * Navegar por SPA les deja datos de la pantalla anterior y rompen al renderizar.
 *
 * El chat sobrevive porque su estado, `isOpen` incluido, ya quedó en
 * localStorage antes de navegar.
 */
const navigateToRoute = (route) => {
  window.location.assign(route);
};

/**
 * Valida las transiciones del state machine
 */
const isValidTransition = (from, to) => {
  const validTransitions = {
    welcome: ['menu'],
    menu: ['section', 'contact'],
    section: ['answer', 'menu', 'section'],
    answer: ['menu', 'section', 'answer'],
    contact: ['contact_confirm', 'menu'],
    contact_confirm: ['menu'],
  };

  return validTransitions[from]?.includes(to) || to === 'welcome';
};

/**
 * Middleware principal del chatbot
 */
const chatbotMiddleware = (store) => (next) => (action) => {
  const prevState = store.getState().chatbot;
  const result = next(action);
  const nextState = store.getState().chatbot;

  switch (action.type) {
    case CHATBOT_NAVIGATE: {
      const { step } = action.payload;

      console.log('🟠 Middleware CHATBOT_NAVIGATE:', {
        from: prevState.currentStep,
        to: step,
      });

      // Validar transición
      if (!isValidTransition(prevState.currentStep, step)) {
        console.warn(
          `⚠️  Transición inválida: ${prevState.currentStep} → ${step}`,
        );
        return result;
      }

      console.log('✅ Transición válida');

      // Persistir estado. Tiene que correr ANTES de navegar: la redirección
      // recarga la página y solo sobrevive lo que ya está en localStorage.
      saveChatHistory({
        isOpen: nextState.isOpen,
        currentStep: nextState.currentStep,
        currentSection: nextState.currentSection,
        currentQuestion: nextState.currentQuestion,
        navigationStack: nextState.navigationStack,
        messages: nextState.messages,
      });

      // Redirección de la app al elegir una sección o una pregunta (NW-2425).
      // Sin permisos no se redirige y el chat sigue su curso normal, así el
      // contenido queda igual disponible como ayuda.
      const target = resolveRedirectTarget(
        action.payload,
        nextState.currentSection,
      );
      const userGroups = store.getState().session?.user?.groups;

      if (target && canAccessRoute(target.requiresRole, userGroups)) {
        navigateToRoute(target.route);
      }

      break;
    }

    case CHATBOT_SUBMIT_CONTACT: {
      // Validar formulario
      const { email, reason } = action.payload;
      const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      const reasonValid = reason.trim().length > 0;

      if (!emailValid || !reasonValid) {
        console.warn('⚠️  Formulario de contacto inválido');
        return result;
      }

      // Dispatch request action
      store.dispatch(chatbot.contactRequest());

      // Call API (reason maps to message)
      services
        .submitContact(email, reason)
        .then(() => {
          store.dispatch(chatbot.contactSuccess());

          // Start 10-second timer
          if (timerInterval) {
            clearInterval(timerInterval);
          }
          timerInterval = setInterval(() => {
            store.dispatch(chatbot.tickTimer());
          }, 1000);
        })
        .catch((error) => {
          const errorMsg =
            error?.data?.message || 'Error al enviar el formulario';
          store.dispatch(chatbot.contactFailure(errorMsg));
        });

      break;
    }

    case CHATBOT_TICK_TIMER: {
      // Si el timer llega a 0, limpiar intervalo
      if (
        nextState.confirmationTimer === 0 ||
        nextState.confirmationTimer === null
      ) {
        if (timerInterval) {
          clearInterval(timerInterval);
          timerInterval = null;
        }
      }
      break;
    }

    case CHATBOT_TOGGLE: {
      // Persistir apertura/cierre para que la recarga de una redirección
      // reabra el chat solo si el usuario lo tenía abierto.
      saveChatHistory({
        isOpen: nextState.isOpen,
        currentStep: nextState.currentStep,
        currentSection: nextState.currentSection,
        currentQuestion: nextState.currentQuestion,
        navigationStack: nextState.navigationStack,
        messages: nextState.messages,
      });
      break;
    }

    case CHATBOT_SET_STEP: {
      // Persistir cambio de paso
      saveChatHistory({
        isOpen: nextState.isOpen,
        currentStep: nextState.currentStep,
        currentSection: nextState.currentSection,
        currentQuestion: nextState.currentQuestion,
        navigationStack: nextState.navigationStack,
        messages: nextState.messages,
      });
      break;
    }

    case CHATBOT_CLEAR_HISTORY: {
      clearChatHistory();
      if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
      }
      break;
    }

    default:
      break;
  }

  return result;
};

export default chatbotMiddleware;
